import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { Copy, Check, Loader2 } from "lucide-react";
import { Eyebrow, FadeIn } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VoucherSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [voucher, setVoucher] = useState(null);
  const [pending, setPending] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!sessionId) return;
    let tries = 0;
    let stopped = false;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/vouchers/session/${sessionId}`);
        if (data.payment_status === "paid") {
          setVoucher(data);
          setPending(false);
          return;
        }
      } catch {
        /* keep polling */
      }
      if (++tries < 12 && !stopped) setTimeout(poll, 2500);
      else setPending(false);
    };
    poll();
    return () => {
      stopped = true;
    };
  }, [sessionId]);

  const copy = () => {
    if (voucher?.code) {
      navigator.clipboard?.writeText(voucher.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const desc = voucher
    ? voucher.kind === "activity"
      ? `${voucher.title} for ${voucher.people} ${voucher.people === 1 ? "person" : "people"}`
      : `€${(voucher.amount / 100).toLocaleString()} towards any adventure`
    : "";

  return (
    <section className="flex min-h-screen items-center bg-navy-950 py-32">
      <div className="mx-auto w-full max-w-2xl px-5 sm:px-8">
        <FadeIn>
          <Eyebrow>Gift Vouchers</Eyebrow>
          {pending && (
            <div className="mt-10 text-center" data-testid="voucher-status-pending">
              <Loader2 className="mx-auto h-8 w-8 animate-spin text-gold" />
              <p className="mt-6 font-serif text-2xl text-ivory">Confirming your payment…</p>
              <p className="mt-2 text-sm font-light text-mist">This only takes a moment.</p>
            </div>
          )}
          {!pending && voucher && (
            <div className="mt-10 text-center" data-testid="voucher-success">
              <h1 className="font-serif text-4xl font-medium text-ivory sm:text-5xl">Your gift voucher is ready!</h1>
              <p className="mt-4 text-sm font-light leading-relaxed text-mist sm:text-base">
                A copy is on its way to <strong className="text-ivory">{voucher.buyer_email}</strong>
                {voucher.recipient_email ? <> and to <strong className="text-ivory">{voucher.recipient_email}</strong></> : ""}.
                Print it, forward it or pop it in a card.
              </p>
              <div className="mx-auto mt-10 max-w-md border-2 border-gold bg-navy-900 p-8" data-testid="voucher-summary">
                <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-gold">The Adventure Islands</p>
                <p className="mt-3 text-sm font-light text-mist">This voucher entitles the bearer to</p>
                <p className="mt-2 font-serif text-2xl text-ivory">{desc}</p>
                <p className="mt-6 text-[11px] font-semibold uppercase tracking-[0.24em] text-mist">Voucher code</p>
                <div className="mt-2 flex items-center justify-center gap-3">
                  <p className="font-mono text-2xl tracking-[0.15em] text-gold" data-testid="voucher-code">{voucher.code}</p>
                  <button type="button" onClick={copy} aria-label="Copy voucher code" data-testid="voucher-copy" className="text-mist transition-colors hover:text-gold">
                    {copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
                  </button>
                </div>
                <p className="mt-6 text-xs text-mist">Valid for 12 months. To book, quote the code on +353 86 251 8252 or info@theadventureislands.com.</p>
              </div>
              <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
                <Link to="/" data-testid="voucher-back-home" className="inline-flex items-center justify-center border border-gold/50 px-9 py-4 text-[12px] font-semibold uppercase tracking-[0.24em] text-gold transition-colors duration-300 hover:bg-gold hover:text-navy-800">
                  Back to the island
                </Link>
                <Link to="/vouchers" className="text-sm font-light text-mist underline-offset-4 transition-colors hover:text-gold hover:underline">
                  Buy another voucher
                </Link>
              </div>
            </div>
          )}
          {!pending && !voucher && (
            <div className="mt-10 text-center" data-testid="voucher-status-unknown">
              <p className="font-serif text-2xl text-ivory">We couldn&rsquo;t find that payment yet.</p>
              <p className="mt-2 text-sm font-light text-mist">If you&rsquo;ve just paid, your voucher is on its way by email — check your inbox in a minute or two.</p>
              <Link to="/" className="mt-8 inline-flex items-center justify-center border border-gold/50 px-9 py-4 text-[12px] font-semibold uppercase tracking-[0.24em] text-gold transition-colors duration-300 hover:bg-gold hover:text-navy-800">
                Back to the island
              </Link>
            </div>
          )}
        </FadeIn>
      </div>
    </section>
  );
}
